import { Component, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { Pulsante } from '../../componenti';
import { ErroreApi } from '../../lib/api';
import { istanteConFuso } from '../../lib/formato';
import { useSessione } from '../../stato/Sessione';

/**
 * 6k — Errore di sistema, con codice di riferimento.
 *
 * «Errori nella forma `{error, message}`; il codice di riferimento è il `ray id` della
 * richiesta, mostrato all'utente e scritto nei log · nessun dettaglio tecnico né stack
 * trace in interfaccia · le scritture sono transazionali: un 500 non lascia convalide
 * parziali · R-25 nessun dato è cancellato.»
 *
 * Due sole cose contano in questa schermata, e vengono prima dell'estetica: dire che cosa
 * **non** è successo — perché chi ha appena premuto «convalida» deve sapere se il lavoro è
 * andato a metà — e consegnare un codice che chi assiste possa cercare nei log.
 */

interface Props {
  /** «Convalida massiva del turno Mattino» — l'atto tentato, non l'endpoint. */
  operazione: string;
  /**
   * Che cosa è rimasto com'era. Va scritta dal chiamante, che è l'unico a sapere cosa
   * stava facendo; il valore predefinito dice il minimo vero in ogni caso.
   */
  conseguenza?: string;
  errore: ErroreApi | Error;
  onRiprova?: () => void;
  /** Etichetta e azione del ritorno: «Torna alle accettazioni». */
  ritorno?: { etichetta: string; onVai: () => void };
}

export function ErroreDiSistema({
  operazione,
  conseguenza = 'Nessuna modifica è stata registrata: lo stato è rimasto quello di prima.',
  errore,
  onRiprova,
  ritorno,
}: Props) {
  const { profilo } = useSessione();
  const [copiato, setCopiato] = useState(false);

  const apiErrore = errore instanceof ErroreApi ? errore : null;
  const codice = apiErrore?.riferimento ?? null;

  async function copia() {
    if (!codice) return;
    try {
      await navigator.clipboard.writeText(codice);
      setCopiato(true);
    } catch {
      // Senza permesso per gli appunti resta la selezione a mano: il codice è a schermo.
    }
  }

  return (
    <div className="guasto">
      <div className="guasto__scheda">
        <div className="guasto__testa">
          <div className="guasto__titolo">Qualcosa non ha funzionato</div>
          <div className="guasto__spiegazione">
            L’operazione «{operazione}» non è andata a buon fine. {conseguenza}
          </div>
        </div>

        <div className="guasto__corpo">
          <div className="guasto__dati">
            {codice ? (
              <>
                <div className="guasto__voce">Codice di riferimento</div>
                <div className="guasto__codice">{codice}</div>
              </>
            ) : null}

            <div className="guasto__voce">Momento</div>
            <div className="guasto__dato">{istanteConFuso()}</div>

            {apiErrore?.operazione ? (
              <>
                <div className="guasto__voce">Operazione</div>
                <div className="guasto__dato">{apiErrore.operazione}</div>
              </>
            ) : null}

            {profilo ? (
              <>
                <div className="guasto__voce">Utenza</div>
                <div className="guasto__dato">{profilo.username}</div>
              </>
            ) : null}
          </div>

          <div className="guasto__consiglio">
            {codice
              ? 'Riprova fra qualche minuto. Se l’errore si ripete, comunica il codice di riferimento a chi segue l’applicazione: identifica con precisione questa esatta richiesta nei log.'
              : 'Riprova fra qualche minuto. Se l’errore si ripete, segnala a chi segue l’applicazione il momento indicato qui sopra.'}
          </div>

          {/* 44 px sul compatto, 60 sul tattile: la stessa misura non serve alle due mani. */}
          <div className="guasto__azioni">
            {onRiprova ? (
              <Pulsante grande onClick={onRiprova}>
                Riprova
              </Pulsante>
            ) : null}
            {codice ? (
              <Pulsante variante="secondario" grande onClick={() => void copia()}>
                {copiato ? 'Codice copiato' : 'Copia il codice'}
              </Pulsante>
            ) : null}
            {ritorno ? (
              <Pulsante variante="neutro" grande onClick={ritorno.onVai}>
                {ritorno.etichetta}
              </Pulsante>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════ confine d'errore

interface StatoConfine {
  errore: Error | null;
}

/**
 * Ciò che 6k copre è il guasto del server. Resta il guasto del client: un'eccezione in
 * fase di disegno lascerebbe la pagina bianca, che è il modo peggiore di fallire — chi
 * guarda non sa se ha sbagliato lui, se il dato è perduto, o a chi rivolgersi.
 *
 * Qui non c'è un ray id da mostrare, perché nessuna richiesta è fallita: si mostra la
 * stessa schermata senza codice, con il momento come unico appiglio.
 */
export class Confine extends Component<{ children: ReactNode }, StatoConfine> {
  state: StatoConfine = { errore: null };

  static getDerivedStateFromError(errore: Error): StatoConfine {
    return { errore };
  }

  componentDidCatch(errore: Error, informazioni: ErrorInfo) {
    // In console, dove serve a chi sviluppa. Mai in interfaccia (6k).
    console.error('Guasto in interfaccia', errore, informazioni.componentStack);
  }

  render() {
    if (!this.state.errore) return this.props.children;
    return (
      <ErroreDiSistema
        operazione="Apertura della schermata"
        conseguenza="Nessun dato è stato modificato."
        errore={this.state.errore}
        onRiprova={() => window.location.reload()}
      />
    );
  }
}
